// src/common/components/zoneMapEditor/hooks/useZoneMapEditor.ts (веб)

import { useCallback, useEffect, useRef, useState } from 'react'
import { TerraDraw, TerraDrawPolygonMode, TerraDrawSelectMode } from 'terra-draw'
import { TerraDrawGoogleMapsAdapter } from 'terra-draw-google-maps-adapter'

type Polygon = Array<{ lat: number; lng: number }>

type Params = {
  center: { lat: number; lng: number }
  initialPolygon?: Polygon
  onSave: (polygon: Polygon) => void
  zoom?: number
}

export const useZoneMapEditor = ({ center, initialPolygon, onSave, zoom = 13 }: Params) => {
  const mapRef = useRef<google.maps.Map | null>(null)
  const drawRef = useRef<TerraDraw | null>(null)
  const [ready, setReady] = useState(false)
  const [mapReady, setMapReady] = useState(false)
  const [hasPolygon, setHasPolygon] = useState(false)

  const isLoaded = typeof window !== 'undefined' && !!window.google?.maps

  const safeCenter = {
    lat: Number(center.lat),
    lng: Number(center.lng),
  }

  const safePolygon: Polygon | undefined = initialPolygon?.map((p) => ({
    lat: Number(p.lat),
    lng: Number(p.lng),
  }))

  const onMapLoad = useCallback((map: google.maps.Map) => {
    mapRef.current = map
    setMapReady(true)
  }, [])

  useEffect(() => {
    if (!mapReady || !mapRef.current) return

    const map = mapRef.current

    const timer = setTimeout(() => {
      const mapDiv = map.getDiv()
      if (mapDiv && !mapDiv.id) {
        mapDiv.id = 'zone-map'
      }

      if (safePolygon && safePolygon.length > 0) {
        const bounds = new google.maps.LatLngBounds()
        safePolygon.forEach((p) => bounds.extend({ lat: p.lat, lng: p.lng }))
        map.fitBounds(bounds, { top: 50, right: 50, bottom: 50, left: 50 })
      } else {
        map.setCenter(safeCenter)
        map.setZoom(zoom)
      }

      const draw = new TerraDraw({
        adapter: new TerraDrawGoogleMapsAdapter({
          map,
          lib: google.maps,
          coordinatePrecision: 9,
        }),
        modes: [
          new TerraDrawPolygonMode(),
          new TerraDrawSelectMode({
            flags: {
              polygon: {
                feature: {
                  draggable: true,
                  coordinates: {
                    draggable: true,
                    deletable: true,
                    // midpoints: false — убираем красные точки на рёбрах
                  },
                },
              },
            },
            styles: {
              selectedPolygonColor: '#1890ff',
              selectedPolygonFillOpacity: 0.2,
              selectedPolygonOutlineColor: '#1890ff',
              selectedPolygonOutlineWidth: 2,
              selectedPointColor: '#1890ff',
              selectedPointWidth: 3,
              selectedPointOutlineColor: '#ffffff',
              selectedPointOutlineWidth: 1,
              selectionPointColor: '#1890ff',
              selectionPointWidth: 3,
              selectionPointOutlineColor: '#ffffff',
              selectionPointOutlineWidth: 1,
            },
          }),
        ],
      })

      draw.start()
      drawRef.current = draw

      // ✅ Ждём ready — это требование Google Maps адаптера [citation:18]
      draw.on('ready', () => {
        // После завершения рисования — переключаем в select
        draw.on('finish', () => {
          setHasPolygon(true)
          draw.setMode('select')
        })

        draw.on('change', () => {
          const snapshot = draw.getSnapshot()
          setHasPolygon(snapshot.some((f) => f.geometry.type === 'Polygon'))
        })

        if (safePolygon && safePolygon.length > 0) {
          draw.addFeatures([
            {
              type: 'Feature',
              geometry: {
                type: 'Polygon',
                coordinates: [
                  [
                    ...safePolygon.map((p) => [p.lng, p.lat]),
                    [safePolygon[0].lng, safePolygon[0].lat],
                  ],
                ],
              },
              properties: {
                mode: 'polygon',
              },
            },
          ])
          setHasPolygon(true)
          draw.setMode('select')
        } else {
          draw.setMode('polygon')
        }

        setReady(true)
      })
    }, 100)

    return () => clearTimeout(timer)
  }, [mapReady, safePolygon, safeCenter.lat, safeCenter.lng, zoom])

  useEffect(() => {
    return () => {
      if (drawRef.current) {
        drawRef.current.stop()
        drawRef.current = null
      }
    }
  }, [])

  const handleClear = useCallback(() => {
    if (drawRef.current) {
      drawRef.current.clear()
      drawRef.current.setMode('polygon')
      setHasPolygon(false)
    }
  }, [])

  const handleSave = useCallback(() => {
    if (!drawRef.current) return
    const snapshot = drawRef.current.getSnapshot()
    const polygonFeature = snapshot.find((f) => f.geometry.type === 'Polygon')

    if (!polygonFeature) return

    const coords = polygonFeature.geometry.coordinates[0] as number[][]
    const points = coords.slice(0, -1).map(([lng, lat]) => ({ lat, lng }))
    onSave(points)
  }, [onSave])

  return {
    isLoaded,
    ready,
    hasPolygon,
    onMapLoad,
    handleClear,
    handleSave,
  }
}
